import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { createBreathTone } from "@/breathTone";

const BREATHS = 3;
const PHASE_MS = 4000;

export const BreathingGuide = ({ onDone }) => {
  const [step, setStep] = useState(0); // even = breathe in, odd = breathe out
  const [ready, setReady] = useState(false);
  const finished = step >= BREATHS * 2;
  const toneRef = useRef(null);
  const [soundOn, setSoundOn] = useState(true);
  const [soundBlocked, setSoundBlocked] = useState(false);

  useEffect(() => {
    const tone = createBreathTone();
    toneRef.current = tone;
    if (!tone) return;
    tone.unlock().catch(() => {});
    const check = () => setSoundBlocked(!tone.isRunning());
    tone.ctx.addEventListener("statechange", check);
    const t = setTimeout(check, 300);
    const unlock = () => tone.unlock().catch(() => {});
    window.addEventListener("pointerdown", unlock);
    return () => {
      clearTimeout(t);
      tone.ctx.removeEventListener("statechange", check);
      window.removeEventListener("pointerdown", unlock);
      tone.fadeOut(0.6);
      tone.close();
    };
  }, []);

  // Each breath phase drives the tone up (in) or down (out).
  useEffect(() => {
    const tone = toneRef.current;
    if (!tone || !ready) return;
    if (finished || !soundOn) return tone.fadeOut(finished ? 1.2 : 0.4);
    tone.breathe(step % 2 === 0, PHASE_MS / 1000);
  }, [step, ready, finished, soundOn]);

  const toggleSound = () => {
    const tone = toneRef.current;
    if (!tone) return;
    tone.unlock().catch(() => {});
    if (soundBlocked) return setSoundOn(true);
    setSoundOn((on) => !on);
  };

  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (finished) {
      const t = setTimeout(onDone, 1200);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStep((s) => s + 1), PHASE_MS);
    return () => clearTimeout(t);
  }, [step, finished, onDone]);

  const inhale = ready && step % 2 === 0 && !finished;
  const label = finished ? "Now, make your wish" : step % 2 === 0 ? "Breathe in" : "Breathe out";

  return (
    <div className="breathing" data-testid="breathing-guide">
      <p className="breathing-intro">Before you make your wish, take three slow breaths with the light</p>
      <div className={`breath-orb ${inhale ? "inhale" : "exhale"} ${finished ? "done" : ""}`} data-testid="breathing-circle">
        <span className="breath-ring" />
        <span className="breath-core" />
        <span className="breath-label" data-testid="breathing-label">{label}</span>
      </div>
      {!finished && (
        <p className="breath-count" data-testid="breathing-count">Breath {Math.floor(step / 2) + 1} of {BREATHS}</p>
      )}
      <button type="button" className="breath-sound" onClick={toggleSound} data-testid="breathing-sound-toggle" aria-label={soundOn ? "Mute breathing tone" : "Play breathing tone"}>
        {soundOn && !soundBlocked ? <Volume2 size={16} /> : <VolumeX size={16} />}
        <span>{soundBlocked ? "Tap to hear the breath" : soundOn ? "Breathing tone on" : "Breathing tone off"}</span>
      </button>
      <button type="button" className="breath-skip" onClick={onDone} data-testid="breathing-skip-button">
        I&apos;m ready to make my wish
      </button>
    </div>
  );
};
